import { sendMsgToApp } from 'businessCommon/src/services';
import { _lang, lang, getCurrentSystem, getCurrentLang, isSystem, isLang } from '@umu/ukit-lang';
import { IEditionItem, IEditionItems, ISchemeComparisonDataItem, SendMsgType } from '@umu/umu-types';
import { PAGE_TYPE, ACTION_TYPE, DEVICE_FROM } from '@umu/umu-api/src/services/payment/paymentEvent';
import { trackPaymentEvent, DISCOUNT_TYPE } from '@umu/umu-api';

import { isInApp } from '@umu/umu-ua';

export const trackEvent = (type: ACTION_TYPE, device: DEVICE_FROM) => {
    trackPaymentEvent({
        page_type: PAGE_TYPE.COMMODITY_UPGRADE_PAGE,
        action_type: type,
        device_from: device,
    });
};

const currentSystem = getCurrentSystem();

export const system = (currentSystem || '').toLowerCase() || 'cn';
const showMarket = isSystem('CN');
const isBasicHideVideo = isSystem('JP') || isSystem('TW');
export const localeLang = (getCurrentLang() || '').toLowerCase() || 'cn';

export const getDiscount = (discount: number, discountType?: DISCOUNT_TYPE) => {
    if (!discount) {
        return '';
    }

    let discountText = (discount * 10).toFixed(1);
    if (_lang('EN') || _lang('JP')) {
        discountText = parseFloat(((1 - discount) * 100).toFixed(12)) + '%';
    }

    if (discountType === DISCOUNT_TYPE.LIMITED_TIME) {
        // 翻译文案里可能带了百分号，去掉一个
        return lang('限时{$0}折', discountText).replace('%%', '%');
    }

    return `${discountText}${lang('折优惠')}`;
};

const isSystemJP = isSystem('JP');

export const basicEdition = (function (): IEditionItem[] {
    if (isSystemJP) {
        return [
            {
                des: lang('学習コンテンツ作成'),
                isCheckMark: 1,
            },
            {
                des: lang('ディスプレイ表示（少人数での双方向学習）'),
                isCheckMark: 1,
            },
            {
                des: lang('アンケート/質問機能'),
                isCheckMark: 1,
            },
            {
                des: lang('試験機能'),
                isCheckMark: 1,
            },
            {
                des: lang('オンラインでの課題提出/添削フィードバック機能'),
                isCheckMark: 1,
            },
        ];
    }
    return [
        {
            des: lang('不限量课程创建'),
            isCheckMark: 1,
        },
        {
            des: lang('30人互动数据'),
            isCheckMark: 1,
        },
        {
            des: isBasicHideVideo ? '' : lang('10次音视频上传试用'),
            isCheckMark: 1,
        },
        {
            des: lang('可创建10个班级'),
            isCheckMark: 1,
        },
        {
            des: lang('视频普通清晰度'),
            isCheckMark: 1,
        },
        {
            des: lang('30人大屏幕互动'),
            isCheckMark: 1,
        },
    ];
})().filter((_) => _.des !== '');

export const professionalEdition = (function (): IEditionItem[] {
    if (isSystemJP) {
        return [
            {
                des: lang('動画アップロード無制限'),
                isCheckMark: 1,
            },
            {
                des: lang('大規模のオンライン講義（双方向学習）'),
                isCheckMark: 1,
            },
            {
                des: lang('受講データ数閲覧無制限'),
                isCheckMark: 1,
            },
            {
                des: lang('クラス作成'),
                isCheckMark: 1,
            },
            {
                des: lang('HD動画フォーマット提供'),
                isCheckMark: 1,
            },
        ];
    }
    return [
        {
            des: lang('包括基本版所有功能'),
            isCheckMark: 1,
        },
        {
            des: lang('不限量互动数据'),
            isCheckMark: 1,
        },
        {
            des: lang('不限量音视频上传'),
            isCheckMark: 1,
        },
        {
            des: lang('不限量班级创建'),
            isCheckMark: 1,
        },
        {
            des: lang('视频高清清晰度'),
            isCheckMark: 1,
        },
        {
            des: lang('不限人数大屏幕互动'),
            isCheckMark: 1,
        },
        {
            des: lang('自定义学习证书'),
            isCheckMark: 1,
        },
    ];
})();

export const eduProfessionalEdition = (function (): IEditionItem[] {
    if (isSystemJP) {
        return [
            {
                des: lang('動画アップロード無制限'),
                isCheckMark: 1,
            },
            {
                des: lang('大規模のオンライン講義（双方向学習）'),
                isCheckMark: 1,
            },
            {
                des: lang('受講データ数閲覧無制限'),
                isCheckMark: 1,
            },
            {
                des: lang('クラス作成'),
                isCheckMark: 1,
            },
            {
                des: lang('HD動画フォーマット提供'),
                isCheckMark: 1,
            },
        ];
    }
    return [
        {
            des: lang('包括基本版所有功能'),
            isCheckMark: 1,
        },
        {
            des: lang('不限量互动数据'),
            isCheckMark: 1,
        },
        {
            des: lang('不限量音视频上传'),
            isCheckMark: 1,
        },
        {
            des: lang('不限量班级创建'),
            isCheckMark: 1,
        },
        {
            des: lang('视频高清清晰度'),
            isCheckMark: 1,
        },
        {
            des: lang('不限人数大屏幕互动'),
            isCheckMark: 1,
        },
    ];
})();

export const businessEdition = (function (): IEditionItem[] {
    if (isSystemJP) {
        return [
            {
                des: lang('組織アカウント管理'),
                isCheckMark: 1,
            },
            {
                des: lang('企業内ナレッジプラットフォームの作成'),
                isCheckMark: 1,
            },
            {
                des: lang('階層/グループ別のアクセス権限制御が可能'),
                isCheckMark: 1,
            },
            {
                des: lang('学習コンテンツ作成・審査・管理機能'),
                isCheckMark: 1,
            },
            {
                des: lang('学習データレポート出力・ダッシュボード機能'),
                isCheckMark: 1,
            },
            {
                des: lang('企業ロゴを入れた専用ポータルサイトの構築'),
                isCheckMark: 1,
            },
        ];
    }
    return [
        {
            des: lang('仅限内部访问权限'),
            isCheckMark: 1,
        },
        {
            des: lang('为学员购买账户，创建学习型组织'),
            isCheckMark: 1,
        },
        {
            des: lang('按部门、分组、班级管理学员，分配学习任务'),
            isCheckMark: 1,
        },
        {
            des: lang('配备管理后台，查看学员明细数据'),
            isCheckMark: 1,
        },
        {
            des: lang('团队个性化 LOGO'),
            isCheckMark: 1,
        },
        {
            des: lang('团队学习首页、运营弹窗搭建'),
            isCheckMark: 1,
        },
        {
            des: lang('课程市场开放，选购第三方课程*').replace('*', '＊'),
            isCheckMark: 1,
            hide: !showMarket,
        },
        {
            des: lang('每日一考功能'),
            isCheckMark: 1,
        },
        {
            des: lang('为付费可选项'),
            isCheckMark: 0,
        },
    ];
})();

export const enterpriseEdition = (function (): IEditionItem[] {
    if (isSystemJP) {
        return [
            {
                des: lang('大規模アカウント管理機能'),
                isCheckMark: 1,
            },
            {
                des: lang('専任のカスタマーサクセス'),
                isCheckMark: 1,
            },
            {
                des: lang('学習デザインコンサルティングサービス*'),
                isCheckMark: 1,
            },
            {
                des: lang('学習データAPI連携*'),
                isCheckMark: 1,
            },
            {
                des: lang('SSO（シングルサインオン）対応'),
                isCheckMark: 1,
            },
            {
                des: lang('プライベートクラウド環境*'),
                isCheckMark: 1,
            },
            {
                des: lang('タレントマネジメント/社員アセスメント機能* '),
                isCheckMark: 1,
            },
            {
                des: lang('オプション機能'),
                isCheckMark: 0,
            },
        ];
    }
    return [
        {
            des: lang('包括团队版所有功能'),
            isCheckMark: 1,
        },
        {
            des: lang('平台访问权限灵活设置'),
            isCheckMark: 1,
        },
        {
            des: lang('专属客户经理服务'),
            isCheckMark: 1,
        },
        {
            des: lang('平台运营方案和项目支持＊'),
            isCheckMark: 1,
        },
        {
            des: lang('SSO 对接、学习数据 API 对接＊'),
            isCheckMark: 1,
        },
        {
            des: lang('数据隔离部署＊'),
            isCheckMark: 1,
        },
        {
            des: lang('课程市场开放，选购第三方课程*').replace('*', '＊'),
            isCheckMark: 1,
            hide: !showMarket,
        },
        {
            des: lang('企业级高级增值功能选购') + '＊',
            isCheckMark: 1,
        },
        {
            des: lang('为付费可选项'),
            isCheckMark: 0,
        },
    ];
})();

export const edition: IEditionItems[] = [
    {
        title: lang('基本版'),
    },
    {
        title: lang('专业版'),
    },
    {
        title: lang('团队版'),
    },
    {
        title: lang('企业版'),
    },
];

/**
 * 打点类型
 * https://docs.google.com/spreadsheets/d/1qUD7k3Dl3FuU_cswVMeaYwIC1efNul1nAIbUswa-1yA/edit#gid=1469618169
 */
export const schemeComparisonData: ISchemeComparisonDataItem[] = [
    {
        title: lang('互动教学工具'),
        tableList: [
            {
                rowname: lang('语音微课'),
                rowList: [
                    {
                        des: lang('语音微课'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('文档上传'),
                rowList: [
                    {
                        des: lang('文档上传'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },

            {
                rowname: lang('考试'),
                rowList: [
                    {
                        des: lang('考试'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },

            {
                rowname: lang('作业（不包括AI功能）'),
                rowList: [
                    {
                        des: lang('作业（不包括AI功能）'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('协同教学'),
                rowList: [
                    {
                        des: lang('协同教学'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('问卷、签到、提问、讨论、抽奖、作业、考试'),
                rowList: [
                    {
                        des: isLang('jp')
                            ? '出席確認、質問、 ディスカッション、アンケート、課題、試験'
                            : lang('问卷、签到、提问、讨论、抽奖、作业、考试'),
                        isCheckMark: 0,
                    },
                    {
                        des: lang('30人数据'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('大屏幕互动'),
                rowList: [
                    {
                        des: lang('大屏幕互动'),
                        isCheckMark: 0,
                    },
                    {
                        des: lang('30人数据'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('视频上传'),
                rowList: [
                    {
                        des: lang('视频上传'),
                        isCheckMark: 0,
                    },
                    {
                        des: isBasicHideVideo ? '' : lang('10次试用'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('视频高清格式'),
                rowList: [
                    {
                        des: lang('视频高清格式'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('作业AI功能'),
                rowList: [
                    {
                        des: lang('作业AI功能'),
                        isCheckMark: 0,
                    },
                    {
                        des: lang('在线购买点数'),
                        isCheckMark: 0,
                    },
                    {
                        des: lang('在线购买点数'),
                        isCheckMark: 0,
                    },
                    {
                        des: lang('在线购买点数'),
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '4',
                    },
                ],
            },
            {
                rowname: lang('AI微课'),
                rowList: [
                    {
                        des: lang('AI微课'),
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 17,
                        action_type: '6',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 18,
                        action_type: '6',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '6',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '6',
                    },
                ],
            },
            {
                rowname: lang('视频、语音微课AI字幕'),
                rowList: [
                    {
                        des: lang('视频、语音微课AI字幕'),
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 17,
                        action_type: '7',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 18,
                        action_type: '7',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '7',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '7',
                    },
                ],
            },
            {
                rowname: lang('自定义学习证书'),
                rowList: [
                    {
                        des: lang('自定义学习证书'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '25',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '25',
                    },
                ],
            },
            {
                rowname: lang('课程重复学习'),
                rowList: [
                    {
                        des: lang('课程重复学习'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '26',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '26',
                    },
                ],
            },
        ],
    },
    {
        title: lang('教学管理'),
        tableList: [
            {
                rowname: lang('创建课程'),
                rowList: [
                    {
                        des: lang('创建课程'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('创建学习项目'),
                rowList: [
                    {
                        des: lang('创建学习项目'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('创建班级'),
                rowList: [
                    {
                        des: lang('创建班级'),
                        isCheckMark: 0,
                    },
                    {
                        des: lang('10个班级'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('互动数据导出'),
                rowList: [
                    {
                        des: lang('互动数据导出'),
                        isCheckMark: 0,
                    },
                    {
                        des: lang('30人数据_v2'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
        ],
    },
    {
        title: lang('组织管理'),
        tableList: [
            {
                rowname: lang('管理后台'),
                rowList: [
                    {
                        des: lang('管理后台'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('自定义 Logo'),
                rowList: [
                    {
                        des: isLang('jp') ? '企業ロゴと専用ポータルサイト' : lang('自定义 Logo'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('子账户管理'),
                rowList: [
                    {
                        des: lang('子账户管理'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('部门、分组设置'),
                rowList: [
                    {
                        des: lang('部门、分组设置'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('企业知识库'),
                rowList: [
                    {
                        des: lang('企业知识库'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('学习首页设置'),
                rowList: [
                    {
                        des: lang('学习首页设置'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('企业后台数据报表统计'),
                rowList: [
                    {
                        des: lang('企业后台数据报表统计'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('运营弹窗'),
                rowList: [
                    {
                        des: lang('运营弹窗'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('每日一考'),
                rowList: [
                    {
                        des: lang('每日一考'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('子管理员'),
                rowList: [
                    {
                        des: lang('子管理员'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '27',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '27',
                    },
                ],
            },
            {
                rowname: lang('微页面'),
                rowList: [
                    {
                        des: lang('微页面'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '8',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '8',
                    },
                ],
            },
            {
                rowname: lang('讲师管理'),
                rowList: [
                    {
                        des: lang('讲师管理'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '9',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '9',
                    },
                ],
            },
            {
                rowname: lang('能力模型'),
                rowList: [
                    {
                        des: lang('能力模型'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '10',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '10',
                    },
                ],
            },
            {
                rowname: lang('学习地图'),
                rowList: [
                    {
                        des: lang('学习地图'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '11',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '11',
                    },
                ],
            },
            {
                rowname: lang('多维度人员测评工具'),
                rowList: [
                    {
                        des: lang('多维度人员测评工具'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '12',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '12',
                    },
                ],
            },
            {
                rowname: lang('水印'),
                rowList: [
                    {
                        des: lang('水印'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '13',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '13',
                    },
                ],
            },
            {
                rowname: lang('IP白名单限制'),
                rowList: [
                    {
                        des: lang('IP白名单限制'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '14',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '14',
                    },
                ],
            },
            {
                rowname: lang('两步验证'),
                rowList: [
                    {
                        des: lang('两步验证'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '15',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '15',
                    },
                ],
            },
            {
                rowname: lang('密码规则自定义'),
                rowList: [
                    {
                        des: lang('密码规则自定义'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '16',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '16',
                    },
                ],
            },
            {
                rowname: lang('平台访问权限'),
                rowList: [
                    {
                        des: lang('平台访问权限'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('仅团队内访问'),
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '17',
                    },
                ],
            },
        ],
    },
    {
        title: lang('其他支持'),
        tableList: [
            {
                rowname: lang('专属客户经理'),
                rowList: [
                    {
                        des: lang('专属客户经理'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 1,
                    },
                ],
            },
            {
                rowname: lang('课程市场开放，选购第三方课程'),
                hide: !showMarket,
                rowList: [
                    {
                        des: lang('课程市场开放，选购第三方课程'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 19,
                        action_type: '18',
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '18',
                    },
                ],
            },
            {
                rowname: lang('学习项目设计指导'),
                rowList: [
                    {
                        des: lang('学习项目设计指导'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '19',
                    },
                ],
            },
            {
                rowname: lang('平台运营方案和项目支持'),
                rowList: [
                    {
                        des: lang('平台运营方案和项目支持'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '20',
                    },
                ],
            },
            {
                rowname: lang('SSO 对接'),
                rowList: [
                    {
                        des: lang('SSO 对接'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '21',
                    },
                ],
            },
            {
                rowname: lang('企业组织架构对接'),
                rowList: [
                    {
                        des: lang('企业组织架构对接'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '22',
                    },
                ],
            },
            {
                rowname: lang('学习数据 API 对接'),
                rowList: [
                    {
                        des: lang('学习数据 API 对接'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '23',
                    },
                ],
            },
            {
                rowname: lang('数据隔离部署'),
                rowList: [
                    {
                        des: lang('数据隔离部署'),
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: '',
                        isCheckMark: 0,
                    },
                    {
                        des: lang('可增购'),
                        href: '/cms/contact-sales',
                        isCheckMark: 0,
                        page_type: 20,
                        action_type: '24',
                    },
                ],
            },
        ],
    },
];

export const applyAppPurchase = (planId: number) => {
    if (isInApp()) {
        sendMsgToApp(SendMsgType.goVersionPurchase, {
            plan_id: planId,
        });
        return true;
    }
    return false;
};
